import React, { useState, useRef, useEffect } from 'react';
import {
  User,
  Camera,
  Upload,
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Building2,
  Sparkles,
  AlertCircle,
  X,
  Check,
  Edit3,
  Save,
  Phone,
  Mail,
  MapPin,
  Lock,
  ChevronRight,
  Trash2,
  RefreshCcw,
  IndianRupee,
  Calendar,
  Briefcase,
  Search
} from 'lucide-react';
import { UserProfile, WalletTransaction, RealMeetup, CompanionServiceRate } from '../types';
import { getRealMeetups, completeRealMeetup } from '../utils/meetupStorage';
import { getDefaultCompanionServices } from '../data/servicesData';
import { CompanionPhotoGallery } from './CompanionPhotoGallery';
import { CompanionServicesManager } from './CompanionServicesManager';
import { DynamicLocationSearchBox } from './location/DynamicLocationSearchBox';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: string | null;
  onUpdateCurrentUser: (name: string) => void;
  onShowToast: (msg: string, type?: 'success' | 'heart' | 'info') => void;
  initialTab?: 'profile' | 'wallet';
  onProfileUpdate?: (profile: UserProfile) => void;
}

const DEFAULT_PROFILE: UserProfile = {
  name: '',
  legalName: '',
  displayName: '',
  phone: '',
  email: '',
  avatarUrl: '',
  gallery: [],
  services: getDefaultCompanionServices(1500),
  role: 'Companion Host & Member',
  city: '',
  bio: '',
  hourlyRate: 1500,
  isIdVerified: false,
  upiId: '',
  bankAccount: '',
  wallet: {
    availableBalance: 0,
    totalEarned: 0,
    pendingEscrow: 0,
    completedMeetups: 0,
    transactions: []
  }
};

const sanitizeProfile = (raw: any): UserProfile => {
  if (!raw) return { ...DEFAULT_PROFILE };
  const hasOldDemoProfile =
    raw.legalName === 'Rahul Kumar Sharma' &&
    raw.phone === '98300 12345' &&
    raw.upiId === 'rahulsharma@okhdfcbank';
  if (hasOldDemoProfile) {
    raw.name = '';
    raw.legalName = '';
    raw.displayName = '';
    raw.phone = '';
    raw.email = '';
    raw.city = '';
    raw.bio = '';
    raw.isIdVerified = false;
    raw.upiId = '';
    raw.bankAccount = '';
    raw.avatarUrl = '';
    raw.gallery = [];
  }
  const hasOldDemoTx = raw.wallet?.transactions?.some(
    (t: any) =>
      t.id === 'TXN-EARN-9821' ||
      t.id === 'TXN-WTH-8812' ||
      t.id === 'TXN-EARN-7419' ||
      t.id === 'TXN-EARN-6320' ||
      t.id === 'TXN-EARN-5102' ||
      String(t.id || '').startsWith('TXN-WTH-') ||
      String(t.id || '').startsWith('TXN-EARN-REAL-')
  );
  if (
    hasOldDemoTx ||
    raw.wallet?.availableBalance === 9200 ||
    raw.wallet?.availableBalance === 14200 ||
    raw.wallet?.availableBalance === 18450 ||
    raw.wallet?.pendingEscrow === 4250
  ) {
    raw.wallet = {
      availableBalance: 0,
      totalEarned: 0,
      pendingEscrow: 0,
      completedMeetups: 0,
      transactions: []
    };
    try {
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(raw));
    } catch (e) {
      console.error(e);
    }
  }

  // Ensure services exist
  if (!raw.services || raw.services.length === 0) {
    raw.services = getDefaultCompanionServices(raw.hourlyRate || 1500);
  }
  // Ensure gallery exists as an array up to 6 photos
  if (!Array.isArray(raw.gallery)) {
    raw.gallery = [raw.avatarUrl || DEFAULT_PROFILE.avatarUrl].filter(Boolean).slice(0, 6);
  } else {
    raw.gallery = Array.from(new Set(raw.gallery.filter(Boolean))).slice(0, 6);
  }

  return raw;
};

const SAMPLE_AVATARS = [
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=600&q=80'
];

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onUpdateCurrentUser,
  onShowToast,
  initialTab = 'profile',
  onProfileUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'wallet'>(initialTab);

  // Load profile from localStorage with fallback (strictly starting at 0 unless real earnings exist)
  const [profile, setProfile] = useState<UserProfile>(() => {
    try {
      const saved = localStorage.getItem('pairup_user_profile_data');
      if (saved) {
        const parsed = sanitizeProfile(JSON.parse(saved));
        if (currentUser) parsed.name = currentUser;
        return parsed;
      }
    } catch (e) {
      console.error(e);
    }
    const init = { ...DEFAULT_PROFILE };
    if (currentUser) init.name = currentUser;
    return init;
  });

  // Keep state in sync whenever modal opens or currentUser changes
  useEffect(() => {
    if (isOpen) {
      try {
        const saved = localStorage.getItem('pairup_user_profile_data');
        if (saved) {
          const parsed = sanitizeProfile(JSON.parse(saved));
          if (currentUser) parsed.name = currentUser;
          setProfile(parsed);
          setEditForm({
            name: parsed.name,
            displayName: parsed.displayName || parsed.name,
            legalName: parsed.legalName || parsed.name,
            phone: parsed.phone,
            email: parsed.email,
            city: parsed.city,
            bio: parsed.bio,
            hourlyRate: parsed.hourlyRate,
            upiId: parsed.upiId,
            role: parsed.role
          });
          setWithdrawUpi(parsed.upiId || '');
        } else {
          const init = { ...DEFAULT_PROFILE, name: currentUser || DEFAULT_PROFILE.name };
          setProfile(init);
        }
      } catch (e) {
        console.error(e);
      }
      setRealMeetups(getRealMeetups());
    }
  }, [isOpen, currentUser]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Profile Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: profile.name,
    displayName: profile.displayName || profile.name,
    legalName: profile.legalName || profile.name,
    phone: profile.phone,
    email: profile.email,
    city: profile.city,
    bio: profile.bio,
    hourlyRate: profile.hourlyRate,
    upiId: profile.upiId,
    role: profile.role
  });

  // Withdrawal State
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawUpi, setWithdrawUpi] = useState<string>(profile.upiId);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);

  // Real Meetup Job State
  const [jobOtpInputs, setJobOtpInputs] = useState<Record<string, string>>({});
  const [completingJobId, setCompletingJobId] = useState<string | null>(null);

  // History Filter
  const [txFilter, setTxFilter] = useState<'all' | 'earning' | 'withdrawal'>('all');

  // Real client companion meetups (stored and synchronized with booking system)
  const [realMeetups, setRealMeetups] = useState<RealMeetup[]>(() => {
    return getRealMeetups();
  });

  // Save profile to localStorage and notify parent helper
  const persistProfile = (updated: UserProfile) => {
    setProfile(updated);
    try {
      localStorage.setItem('pairup_user_profile_data', JSON.stringify(updated));
    } catch (e) {
      console.error(e);
    }
    onProfileUpdate?.(updated);
  };

  // Photos Gallery Handlers (Multi-photo upload, primary avatar select, delete)
  const handleSetPrimaryAvatar = (url: string) => {
    const updated = { ...profile, avatarUrl: url };
    persistProfile(updated);
    onShowToast('Set as primary profile photo! 📸', 'success');
  };

  const handleAddPhotos = (newUrls: string[]) => {
    const current = profile.gallery && profile.gallery.length > 0 ? profile.gallery : [profile.avatarUrl];
    const merged = Array.from(new Set([...current, ...newUrls])).filter(Boolean);
    const updated = {
      ...profile,
      gallery: merged,
      avatarUrl: profile.avatarUrl || newUrls[0]
    };
    persistProfile(updated);
    onShowToast(`Added ${newUrls.length} photo${newUrls.length > 1 ? 's' : ''} to your personal gallery! 📸`, 'success');
  };

  const handleDeletePhoto = (url: string) => {
    const current = profile.gallery && profile.gallery.length > 0 ? profile.gallery : [profile.avatarUrl];
    const filtered = current.filter((p) => p !== url);
    let newAvatar = profile.avatarUrl;
    if (profile.avatarUrl === url) {
      newAvatar = filtered[0] || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80';
    }
    const updated = {
      ...profile,
      gallery: filtered,
      avatarUrl: newAvatar
    };
    persistProfile(updated);
    onShowToast('Photo removed from your personal gallery', 'info');
  };

  const handleSelectPresetAvatar = (url: string) => {
    const current = profile.gallery && profile.gallery.length > 0 ? profile.gallery : [profile.avatarUrl];
    const updatedGallery = current.includes(url) ? current : [...current, url];
    const updated = { ...profile, avatarUrl: url, gallery: updatedGallery };
    persistProfile(updated);
    onShowToast('Profile photo updated!', 'success');
  };

  const handleResetPhoto = () => {
    const defaultImg = 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80';
    const updated = {
      ...profile,
      avatarUrl: defaultImg
    };
    persistProfile(updated);
    onShowToast('Profile photo reset to default', 'info');
  };

  // Services Offered & Rates Handler
  const handleChangeServices = (updatedServices: CompanionServiceRate[]) => {
    const updated = {
      ...profile,
      services: updatedServices
    };
    persistProfile(updated);
  };

  // 2. Save Profile Information
  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedName = (editForm.displayName || editForm.name).trim() || profile.name;
    const updated: UserProfile = {
      ...profile,
      name: updatedName,
      displayName: (editForm.displayName || editForm.name).trim() || profile.displayName,
      legalName: profile.legalName || editForm.legalName || profile.name,
      phone: editForm.phone.trim() || profile.phone,
      email: editForm.email.trim() || profile.email,
      city: editForm.city.trim() || profile.city,
      bio: editForm.bio.trim() || profile.bio,
      hourlyRate: Number(editForm.hourlyRate) || profile.hourlyRate,
      upiId: editForm.upiId.trim() || profile.upiId,
      role: editForm.role as any
    };
    persistProfile(updated);
    onUpdateCurrentUser(updated.displayName || updated.name);
    setIsEditing(false);
    onShowToast('Profile information updated successfully! ✨', 'success');
  };

  // 3. Instant Withdrawal Request (Requires authoritative backend payout processing)
  const handleWithdrawal = (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawError('');
    setWithdrawSuccess(false);
    const amt = parseFloat(withdrawAmount);

    if (isNaN(amt) || amt <= 0) {
      setWithdrawError('Please enter a valid withdrawal amount');
      return;
    }

    if (amt < 500) {
      setWithdrawError('Minimum withdrawal amount is ₹500');
      return;
    }

    if (amt > profile.wallet.availableBalance) {
      setWithdrawError(`Insufficient balance! Your wallet has ₹${profile.wallet.availableBalance.toLocaleString('en-IN')}`);
      return;
    }

    if (!withdrawUpi.trim()) {
      setWithdrawError('Please enter your UPI ID');
      return;
    }

    setIsWithdrawing(false);
    setWithdrawError(
      'Direct client-side wallet withdrawals are disabled for security. All payouts are processed through the verified Companion Dashboard and authorized backend settlement ledger.'
    );
  };

  const handleCompleteRealMeetup = (
    _meetupId: string,
    _expectedOtp: string,
    _amount: number,
    _title: string,
    _client: string,
    _category: string
  ) => {
    setCompletingJobId(null);
    onShowToast(
      'Meetup OTP verification must be completed through the server-verified booking detail page. Client-side wallet credits are disabled.',
      'info'
    );
  };

  if (!isOpen) return null;

  const filteredTransactions = profile.wallet.transactions.filter((tx) => {
    if (txFilter === 'all') return true;
    return tx.type === txFilter;
  });

  return (
    <div
      id="user-profile-modal"
      className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-pink-100 flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-purple-700 text-white p-4 sm:p-6 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-xs border border-white/20">
              {activeTab === 'profile' ? (
                <User className="w-5 h-5 text-white" />
              ) : (
                <Wallet className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="font-extrabold text-base sm:text-lg text-white leading-tight flex items-center gap-2 flex-wrap">
                <span>{profile.name || 'User Profile'}</span>
                {profile.isIdVerified ? (
                  <span className="relative inline-flex items-center gap-1.5 text-[11px] bg-emerald-400/30 text-emerald-100 border border-emerald-300/50 px-2.5 py-0.5 rounded-full font-extrabold shadow-xs">
                    <ShieldCheck className="w-3 h-3 text-emerald-200" />
                    <span>Verified Companion</span>
                  </span>
                ) : (
                  <span className="relative inline-flex items-center gap-1.5 text-[11px] bg-amber-400/30 text-amber-100 border border-amber-300/50 px-2.5 py-0.5 rounded-full font-extrabold shadow-xs">
                    <Clock className="w-3 h-3 text-amber-200" />
                    <span>Verification Pending</span>
                  </span>
                )}
              </h2>
              <p className="text-xs text-pink-100 mt-0.5">
                {activeTab === 'profile'
                  ? 'User Profile & Photo Management'
                  : 'Work Earnings & Wallet Management'}
              </p>
            </div>
          </div>

          <button
            type="button"
            id="close-profile-modal-btn"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-4 sm:px-6 pt-2 shrink-0 gap-2">
          <button
            type="button"
            id="profile-tab-btn"
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 py-2.5 px-4 font-bold text-xs sm:text-sm rounded-t-xl transition-all cursor-pointer border-b-2 ${
              activeTab === 'profile'
                ? 'border-pink-600 text-pink-600 bg-white shadow-2xs'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>Profile & Photo</span>
          </button>

          <button
            type="button"
            id="wallet-tab-btn"
            onClick={() => setActiveTab('wallet')}
            className={`flex items-center gap-2 py-2.5 px-4 font-bold text-xs sm:text-sm rounded-t-xl transition-all cursor-pointer border-b-2 ${
              activeTab === 'wallet'
                ? 'border-pink-600 text-pink-600 bg-white shadow-2xs'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Wallet className="w-4 h-4" />
            <span>Earnings Wallet</span>
            <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black px-1.5 py-0.5 rounded-md ml-1">
              ₹{profile.wallet.availableBalance.toLocaleString('en-IN')}
            </span>
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-6">
          {/* ================= TAB 1: PROFILE & PHOTO ================= */}
          {activeTab === 'profile' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Photo & Multi-Image Gallery */}
              <CompanionPhotoGallery
                avatarUrl={profile.avatarUrl}
                gallery={Array.isArray(profile.gallery) ? profile.gallery : [profile.avatarUrl].filter(Boolean)}
                userName={profile.name}
                isIdVerified={profile.isIdVerified}
                onSetPrimaryAvatar={handleSetPrimaryAvatar}
                onAddPhotos={handleAddPhotos}
                onDeletePhoto={handleDeletePhoto}
                onUpdateGallery={(newGallery, newAvatar) => {
                  const updated = {
                    ...profile,
                    gallery: newGallery,
                    avatarUrl: newAvatar !== undefined ? newAvatar : profile.avatarUrl,
                  };
                  persistProfile(updated);
                }}
                onSelectPresetAvatar={handleSelectPresetAvatar}
                onResetPhoto={handleResetPhoto}
                onShowToast={onShowToast}
              />

              {/* Profile Details Form */}
              <div className="bg-white rounded-3xl border border-slate-200 p-5 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-600" />
                    <h4 className="text-sm font-bold text-slate-900">Personal & Companion Details</h4>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditing) {
                        setEditForm({
                          name: profile.name,
                          displayName: profile.displayName || profile.name,
                          legalName: profile.legalName || profile.name,
                          phone: profile.phone,
                          email: profile.email,
                          city: profile.city,
                          bio: profile.bio,
                          hourlyRate: profile.hourlyRate,
                          upiId: profile.upiId,
                          role: profile.role
                        });
                      }
                      setIsEditing(!isEditing);
                    }}
                    className="text-xs font-bold text-pink-600 hover:text-pink-700 flex items-center gap-1 cursor-pointer bg-pink-50 px-2.5 py-1 rounded-lg"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>{isEditing ? 'Cancel Edit' : 'Edit Details'}</span>
                  </button>
                </div>

                {isEditing ? (
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    {/* Identity Privacy Info */}
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px]">
                      <div>
                        <span className="font-bold text-slate-800 flex items-center gap-1">
                          <Lock className="w-3.5 h-3.5 text-slate-500" /> Full Legal Name (ID অনুযায়ী):{' '}
                          <span className="text-purple-700">{profile.legalName || profile.name}</span>
                        </span>
                        <span className="text-[10px] text-slate-400">
                          🔒 Kept private for KYC, payouts & compliance. Not shown on public profile.
                        </span>
                      </div>
                      {profile.isIdVerified ? (
                        <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-bold text-[10px] border border-sky-200 shrink-0 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-sky-600" /> Verified ✓
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold text-[10px] border border-amber-200 shrink-0 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-amber-600" /> KYC Pending
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-bold text-slate-700">
                            Profile Name / Display Name *
                          </label>
                          <span className="text-[10px] text-purple-600 font-semibold">👉 Public</span>
                        </div>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Rahul, Alex, Travel Buddy Kolkata"
                          value={editForm.displayName || editForm.name}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              displayName: e.target.value,
                              name: e.target.value
                            })
                          }
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-400">
                          Public profile-এ অন্যরা শুধু এই নাম দেখবে।
                        </span>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Phone Number *</label>
                        <input
                          type="text"
                          required
                          value={editForm.phone}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Email Address *</label>
                        <input
                          type="email"
                          required
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
                          <span>Primary Living City / Location *</span>
                          <span className="text-[10px] text-pink-600 font-semibold">Google Places (IN)</span>
                        </label>
                        <DynamicLocationSearchBox
                          placeholder="Search your living city, area, or PIN"
                          selectedLocation={
                            editForm.city
                              ? {
                                  place_id: `place_${editForm.city}`,
                                  formatted_address: editForm.city,
                                  latitude: 22.58,
                                  longitude: 88.46,
                                  city: editForm.city,
                                  country: 'India',
                                  country_code: 'IN',
                                }
                              : null
                          }
                          onLocationSelect={(loc) => {
                            if (loc) {
                              setEditForm({ ...editForm, city: loc.formatted_address || loc.city || '' });
                            }
                          }}
                          size="sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Hourly Fee / Rate (₹)
                        </label>
                        <input
                          type="number"
                          value={editForm.hourlyRate}
                          onChange={(e) => setEditForm({ ...editForm, hourlyRate: Number(e.target.value) })}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Withdrawal UPI ID (For payouts) *
                        </label>
                        <input
                          type="text"
                          required
                          value={editForm.upiId}
                          onChange={(e) => setEditForm({ ...editForm, upiId: e.target.value })}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">Bio & Description</label>
                      <textarea
                        rows={2}
                        value={editForm.bio}
                        onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-2 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-xl shadow-md transition-colors cursor-pointer flex items-center gap-1.5"
                      >
                        <Save className="w-3.5 h-3.5" />
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    {/* Public Profile Name & Verified Badge Banner */}
                    <div className="sm:col-span-2 p-3.5 rounded-2xl bg-gradient-to-r from-purple-50 via-pink-50 to-slate-50 border border-purple-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-base font-black text-slate-900">
                            {profile.displayName || profile.name || 'Not set'}
                          </span>
                          {profile.isIdVerified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-100 text-sky-800 border border-sky-200 text-xs font-bold">
                              <CheckCircle2 className="w-3.5 h-3.5 text-sky-600" />
                              <span>Verified ✓</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-xs font-bold">
                              <Clock className="w-3.5 h-3.5 text-amber-600" />
                              <span>KYC Pending</span>
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          👉 <strong>Public Profile Name:</strong> This is what clients and other users see.
                        </p>
                      </div>
                      <div className="text-left sm:text-right text-[11px] text-slate-500 shrink-0">
                        <span className="font-bold text-slate-800 flex items-center gap-1">
                          <Lock className="w-3 h-3 text-slate-400" /> Legal Name: {profile.legalName || profile.name}
                        </span>
                        <span className="text-[10px] text-slate-400">100% Private (KYC & Payouts)</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <span className="text-slate-400 font-semibold block">Phone Number:</span>
                      <p className="font-bold text-slate-800 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-pink-600" />
                        <span>+91 {profile.phone}</span>
                      </p>
                    </div>

                    <div className="space-y-1">
                      <span className="text-slate-400 font-semibold block">Email Address:</span>
                      <p className="font-bold text-slate-800 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-purple-600" />
                        <span>{profile.email}</span>
                      </p>
                    </div>

                    <div className="space-y-1">
                      <span className="text-slate-400 font-semibold block">City & Location:</span>
                      <p className="font-bold text-slate-800 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-rose-500" />
                        <span>{profile.city}</span>
                      </p>
                    </div>

                    <div className="space-y-1">
                      <span className="text-slate-400 font-semibold block">Hourly Rate:</span>
                      <p className="font-bold text-slate-800 flex items-center gap-1">
                        <IndianRupee className="w-3.5 h-3.5 text-emerald-600" />
                        <span>₹{profile.hourlyRate.toLocaleString('en-IN')} / hour</span>
                      </p>
                    </div>

                    <div className="sm:col-span-2 space-y-1 bg-slate-50 p-3 rounded-2xl border border-slate-100">
                      <span className="text-slate-400 font-semibold block">Withdrawal UPI ID:</span>
                      <p className="font-mono font-bold text-slate-900">{profile.upiId}</p>
                    </div>

                    <div className="sm:col-span-2 space-y-1">
                      <span className="text-slate-400 font-semibold block">Bio & Description:</span>
                      <p className="text-slate-700 leading-relaxed italic">{profile.bio}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Services Offered & Hourly Rates Management */}
              <CompanionServicesManager
                services={profile.services || getDefaultCompanionServices(profile.hourlyRate || 1500)}
                onChangeServices={handleChangeServices}
                onShowToast={onShowToast}
              />
            </div>
          )}

          {/* ================= TAB 2: EARNINGS WALLET ================= */}
          {activeTab === 'wallet' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Earnings Overview Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                {/* 1. Available Balance */}
                <div className="bg-gradient-to-br from-emerald-500 via-teal-600 to-emerald-700 text-white rounded-3xl p-5 shadow-lg shadow-emerald-500/15 relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-15">
                    <Wallet className="w-16 h-16" />
                  </div>
                  <span className="text-xs text-emerald-100 font-bold uppercase tracking-wider block">
                    Available Balance
                  </span>
                  <div className="text-2xl sm:text-3xl font-black mt-1 flex items-baseline gap-1">
                    <span>₹{profile.wallet.availableBalance.toLocaleString('en-IN')}</span>
                  </div>
                  <span className="text-[11px] text-emerald-100/90 mt-2 block flex items-center gap-1">
                    {profile.wallet.availableBalance > 0 ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Ready for bank transfer
                      </>
                    ) : (
                      <>
                        <Clock className="w-3.5 h-3.5 text-emerald-200" /> Auto-credited upon completing meetups
                      </>
                    )}
                  </span>
                </div>

                {/* 2. Total Work Earnings */}
                <div className="bg-slate-900 text-white rounded-3xl p-5 shadow-lg border border-slate-800 relative overflow-hidden">
                  <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">
                    Total Earnings
                  </span>
                  <div className="text-2xl sm:text-3xl font-black mt-1 text-pink-400">
                    ₹{profile.wallet.totalEarned.toLocaleString('en-IN')}
                  </div>
                  <span className="text-[11px] text-slate-400 mt-2 block">
                    Completed: <strong className="text-white">{profile.wallet.completedMeetups} Meetups</strong>
                  </span>
                </div>

                {/* 3. Escrow Pending Balance */}
                <div className="bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 text-white rounded-3xl p-5 shadow-lg border border-purple-800/40 relative overflow-hidden">
                  <span className="text-xs text-purple-200 font-bold uppercase tracking-wider block">
                    Pending in Escrow
                  </span>
                  <div className="text-2xl sm:text-3xl font-black mt-1 text-purple-300">
                    ₹{profile.wallet.pendingEscrow.toLocaleString('en-IN')}
                  </div>
                  <span className="text-[11px] text-purple-200 mt-2 block flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5 text-purple-300" />
                    {profile.wallet.pendingEscrow > 0
                      ? 'Released when meetup OTP is verified'
                      : 'Held securely for active bookings'}
                  </span>
                </div>
              </div>

              {/* Instant Withdrawal Card */}
              <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-xs space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                    <div>
                      <h4 className="text-sm font-black text-slate-900">
                        Instant Withdrawal
                      </h4>
                      <p className="text-[11px] text-slate-500">
                        Transfer your earnings directly to any verified UPI ID or bank account
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                    0% Fee • Instant
                  </span>
                </div>

                <form onSubmit={handleWithdrawal} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Withdrawal Amount (₹) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-2.5 text-slate-400 font-bold text-sm">₹</span>
                      <input
                        type="number"
                        min={500}
                        max={profile.wallet.availableBalance}
                        value={withdrawAmount}
                        onChange={(e) => {
                          setWithdrawAmount(e.target.value);
                          setWithdrawError('');
                        }}
                        disabled={profile.wallet.availableBalance <= 0}
                        className="w-full pl-8 pr-4 py-2.5 text-sm font-bold text-slate-900 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50/60 disabled:opacity-60 disabled:cursor-not-allowed"
                        placeholder={profile.wallet.availableBalance > 0 ? "e.g. 2000" : "Balance ₹0"}
                      />
                    </div>

                    {/* Quick Select Preset Pills or Zero Balance Notice */}
                    {profile.wallet.availableBalance > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[1000, 2500, 5000, profile.wallet.availableBalance]
                          .filter((p, idx, arr) => p <= profile.wallet.availableBalance && arr.indexOf(p) === idx)
                          .map((preset, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setWithdrawAmount(preset.toString());
                                setWithdrawError('');
                              }}
                              className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 border border-slate-200 transition-colors cursor-pointer"
                            >
                              {preset === profile.wallet.availableBalance ? 'All (Full)' : `₹${preset.toLocaleString('en-IN')}`}
                            </button>
                          ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded-xl border border-amber-200/60 mt-2">
                        💡 Current wallet balance is ₹0. When a meetup is completed, your earnings are credited here instantly for withdrawal.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Payout UPI ID (Google Pay, PhonePe, Paytm, BHIM) *
                    </label>
                    <input
                      type="text"
                      required
                      value={withdrawUpi}
                      onChange={(e) => {
                        setWithdrawUpi(e.target.value);
                        setWithdrawError('');
                      }}
                      className="w-full px-3.5 py-2.5 text-xs sm:text-sm font-mono text-slate-900 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50/60"
                      placeholder="username@okhdfcbank"
                    />
                  </div>

                  {withdrawError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-xs text-red-600 font-medium">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{withdrawError}</span>
                    </div>
                  )}

                  {withdrawSuccess && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-xs text-emerald-700 font-bold">
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                      <span>Withdrawal request successful! Money has been transferred to your account.</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isWithdrawing || profile.wallet.availableBalance <= 0}
                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-extrabold text-xs sm:text-sm rounded-2xl shadow-lg shadow-emerald-600/20 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isWithdrawing ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Processing bank transfer...</span>
                      </>
                    ) : profile.wallet.availableBalance <= 0 ? (
                      <>
                        <Lock className="w-4 h-4" />
                        <span>No balance available (Withdraw once earnings are credited)</span>
                      </>
                    ) : (
                      <>
                        <ArrowUpRight className="w-4 h-4" />
                        <span>Confirm Withdrawal (₹{withdrawAmount || profile.wallet.availableBalance})</span>
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Scheduled Bookings & In-Person Meetups Module */}
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-3 border-b border-slate-200/80">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-pink-600 shrink-0" />
                    <div>
                      <h4 className="text-xs sm:text-sm font-black text-slate-900">
                        Active Bookings & Scheduled Meetups
                      </h4>
                      <p className="text-[11px] text-slate-500">
                        Confirmed bookings and live sessions appear here. Earnings are released to your wallet upon client OTP verification.
                      </p>
                    </div>
                  </div>
                  {realMeetups.length > 0 && (
                    <span className="text-[10px] font-bold text-pink-700 bg-pink-100 px-2.5 py-1 rounded-full shrink-0 self-start sm:self-auto">
                      {realMeetups.filter(m => m.status === 'pending').length} Active Job(s)
                    </span>
                  )}
                </div>

                {realMeetups.length === 0 ? (
                  <div className="p-7 text-center space-y-3 bg-white rounded-2xl border border-dashed border-slate-200">
                    <div className="w-12 h-12 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center mx-auto mb-1 shadow-xs">
                      <Calendar className="w-6 h-6" />
                    </div>
                    <h5 className="text-sm font-bold text-slate-800">No Scheduled Bookings</h5>
                    <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                      You currently have no pending bookings. When a client confirms a booking with you, the details will appear here. After completing the session, enter the client's 4-digit verification OTP to receive your payout.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        const el = document.getElementById('companions-section');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer"
                    >
                      <Search className="w-3.5 h-3.5" />
                      <span>Explore Companions & Book a Session</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {realMeetups.map((job) => {
                      const isDone = job.status === 'completed';
                      const isProcessing = completingJobId === job.id;

                      return (
                        <div
                          key={job.id}
                          className={`p-4 rounded-2xl border transition-all ${
                            isDone
                              ? 'bg-emerald-50/70 border-emerald-200 opacity-90'
                              : 'bg-white border-slate-200 hover:border-pink-300 shadow-xs'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-xs text-slate-900">{job.title}</span>
                                <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-semibold">
                                  {job.serviceCategory}
                                </span>
                                {isDone ? (
                                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Credited to Wallet
                                  </span>
                                ) : (
                                  <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Lock className="w-3 h-3 text-amber-600" /> Pending OTP Verification
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Companion: <strong className="text-slate-700">{job.companionName}</strong> • Client: <strong className="text-slate-700">{job.clientName}</strong> • {job.location}, {job.city} ({job.hours} hrs @ ₹{job.ratePerHour}/hr)
                              </p>
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                Date & Time: {job.date} at {job.time}
                              </p>
                            </div>

                            <div className="text-right shrink-0">
                              <span className="text-base sm:text-lg font-black text-emerald-600 block">
                                +₹{job.totalAmount.toLocaleString('en-IN')}
                              </span>
                              <span className="text-[10px] text-slate-400">Session Earnings</span>
                            </div>
                          </div>

                          {!isDone && (
                            <div className="pt-2.5 border-t border-slate-100 flex flex-col sm:flex-row items-center gap-2">
                              <div className="relative w-full sm:w-64">
                                <input
                                  type="text"
                                  maxLength={6}
                                  placeholder="Enter Client SMS OTP"
                                  value={jobOtpInputs[job.id] || ''}
                                  onChange={(e) => {
                                    setJobOtpInputs({
                                      ...jobOtpInputs,
                                      [job.id]: e.target.value
                                    });
                                  }}
                                  className="w-full px-3 py-1.5 text-xs font-mono font-bold tracking-wider border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                                />
                              </div>

                              <button
                                type="button"
                                disabled={isProcessing || !(jobOtpInputs[job.id] || '').trim()}
                                onClick={() =>
                                  handleCompleteRealMeetup(
                                    job.id,
                                    job.expectedOtp,
                                    job.totalAmount,
                                    job.title,
                                    `${job.clientName} (${job.location})`,
                                    job.serviceCategory
                                  )
                                }
                                className="w-full sm:w-auto px-4 py-1.5 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                              >
                                <Sparkles className="w-3.5 h-3.5 text-pink-200" />
                                <span>Verify via Booking Details</span>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-[10px] text-slate-500 bg-white p-2.5 rounded-xl border border-slate-200/60 leading-relaxed">
                  💡 <strong>Escrow & Payout Security:</strong> All client payments are protected in secure escrow and released to your wallet immediately when the 4-digit verification code is confirmed at the end of the meetup.
                </p>
              </div>

              {/* Work Transaction History Ledger */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs sm:text-sm font-black text-slate-900 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-pink-600" />
                    <span>Earnings Ledger & Transaction History</span>
                  </h4>

                  {/* Filter tabs */}
                  <div className="flex items-center bg-slate-100 p-0.5 rounded-xl text-[11px] font-bold">
                    <button
                      type="button"
                      onClick={() => setTxFilter('all')}
                      className={`px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${
                        txFilter === 'all' ? 'bg-white text-slate-900 shadow-2xs' : 'text-slate-500'
                      }`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setTxFilter('earning')}
                      className={`px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${
                        txFilter === 'earning' ? 'bg-white text-emerald-700 shadow-2xs' : 'text-slate-500'
                      }`}
                    >
                      Earnings
                    </button>
                    <button
                      type="button"
                      onClick={() => setTxFilter('withdrawal')}
                      className={`px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${
                        txFilter === 'withdrawal' ? 'bg-white text-rose-700 shadow-2xs' : 'text-slate-500'
                      }`}
                    >
                      Withdrawals
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-slate-100 border border-slate-200 rounded-3xl overflow-hidden bg-white shadow-2xs">
                  {filteredTransactions.length === 0 ? (
                    <div className="p-8 text-center space-y-2 bg-slate-50/50">
                      <div className="w-12 h-12 rounded-full bg-emerald-100/70 text-emerald-600 flex items-center justify-center mx-auto mb-1">
                        <Wallet className="w-6 h-6" />
                      </div>
                      <p className="text-sm font-bold text-slate-800">Transactions</p>
                      <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                        When you complete a meetup with a client and verify their 4-digit confidential OTP, your earnings and withdrawal history will be reflected here automatically.
                      </p>
                    </div>
                  ) : (
                    filteredTransactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="p-3.5 sm:p-4 flex items-center justify-between gap-3 hover:bg-slate-50/80 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-2xl flex items-center justify-center shrink-0 ${
                              tx.type === 'earning'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-rose-100 text-rose-700'
                            }`}
                          >
                            {tx.type === 'earning' ? (
                              <ArrowDownLeft className="w-5 h-5" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs sm:text-sm font-bold text-slate-900 truncate">
                              {tx.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                              <span>{tx.clientOrBeneficiary}</span>
                              <span>•</span>
                              <span>{tx.date} at {tx.time}</span>
                              {tx.meetOtp && (
                                <>
                                  <span>•</span>
                                  <span className="text-emerald-700 font-mono font-bold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                                    OTP: {tx.meetOtp} ✓
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <p
                            className={`text-xs sm:text-sm font-black ${
                              tx.type === 'earning' ? 'text-emerald-600' : 'text-slate-900'
                            }`}
                          >
                            {tx.type === 'earning' ? `+₹${tx.amount.toLocaleString('en-IN')}` : `-₹${tx.amount.toLocaleString('en-IN')}`}
                          </p>
                          <span className="text-[10px] text-emerald-600 font-semibold block">
                            ✓ {tx.status === 'completed' ? 'Completed' : 'Processing'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 bg-slate-50 border-t border-slate-100 text-center shrink-0 flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1.5 text-emerald-700 font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-600" /> 100% Escrow Protected & Verified Wallet
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
